import api from "../../../api/axios";

// Feature-local API for the realty detail surface (Phase R3).
//
// Per CLAUDE.md SR-6 (Local Duplication Is Preferred): the detail
// feature does NOT import from features/realtyList — that would
// violate "Feature ownership" boundary. R3.10A locally duplicates
// togglePropertyLike / togglePropertyBookmark verbatim from the
// realtyList wrapper rather than sharing.
//
// Backend reference:
//   GET    /api/realty/properties/{id}/           PropertyDetailSerializer
//                                                 (location, residential|land|
//                                                  commercial, images, features,
//                                                  views_count, likes_count,
//                                                  bookmarks_user_count,
//                                                  bookmarks_anon_count,
//                                                  created_by, liked_by_me,
//                                                  bookmarked_by_me)
//   POST   /api/realty/properties/{id}/view/      log_view → 204 (best-effort
//                                                 from this surface only)
//   POST   /api/realty/properties/{id}/like/      toggle_like     → {liked: bool}
//   POST   /api/realty/properties/{id}/bookmark/  toggle_bookmark → {bookmarked: bool}
//   PATCH  /api/realty/properties/{id}/           partial_update  (status field gated
//                                                  by PropertyWriteSerializer.validate_status —
//                                                  blocks archived → non-archived for non-staff)
//   DELETE /api/realty/properties/{id}/           destroy → 204
//
// R3.12A / R8.6 — social + moderation endpoints consumed by the detail
// surface. Follow is NOT wrapped here: R8.6 routed detail follow
// through the canonical Redux thunk (actions/userActions.followUser)
// so the mutation propagates via USER_LOGIN_SUCCESS into
// state.userLogin.userInfo.following, and every selectFollowGraph
// subscriber (CreateChatModal, AddMembersModal, UserScreen) updates
// without a hard reload. SR-6 still applies to complaints — those are
// screen-local mutations with no cross-surface propagation.
//   GET    /api/complaints/categories/            ComplaintCategorySerializer[]
//   POST   /api/complaints/                       {category_id, description?,
//                                                  target_property_id} →
//                                                 ComplaintDetailSerializer (201)

const REALTY_BASE = "/realty";

export const fetchPropertyDetail = (id) =>
  api.get(`${REALTY_BASE}/properties/${id}/`).then((res) => res.data);

// PROPERTY-VIEW-TRACKING-A1 — feature-local wrapper. Call only after the
// canonical GET /realty/:id detail request succeeds. Best-effort: errors
// are swallowed so a failed log cannot replace, toast, or alter the loaded
// page. Module-level 24h map absorbs StrictMode replay / remount / retry
// for the same property id; backend remains the identity/dedup authority.
const PROPERTY_VIEW_LOG_WINDOW_MS = 24 * 60 * 60 * 1000;
const loggedPropertyViews = new Map();

function pruneLoggedPropertyViews(now) {
  loggedPropertyViews.forEach((loggedAt, key) => {
    if (now - loggedAt >= PROPERTY_VIEW_LOG_WINDOW_MS) {
      loggedPropertyViews.delete(key);
    }
  });
}

export function _resetLoggedPropertyViewsForTests() {
  loggedPropertyViews.clear();
}

export function _seedLoggedPropertyViewForTests(id, loggedAt) {
  loggedPropertyViews.set(String(id), loggedAt);
}

export const logPropertyView = (id) =>
  api.post(`${REALTY_BASE}/properties/${id}/view/`).then((res) => res.data);

export function logPropertyViewBestEffort(id) {
  if (id === null || id === undefined || id === "") {
    return Promise.resolve();
  }
  const key = String(id);
  const now = Date.now();
  pruneLoggedPropertyViews(now);
  if (loggedPropertyViews.has(key)) {
    return Promise.resolve();
  }
  loggedPropertyViews.set(key, now);
  return logPropertyView(id).catch(() => undefined);
}

export const togglePropertyLike = (id) =>
  api.post(`${REALTY_BASE}/properties/${id}/like/`).then((res) => res.data);

export const togglePropertyBookmark = (id) =>
  api
    .post(`${REALTY_BASE}/properties/${id}/bookmark/`)
    .then((res) => res.data);

// R3.11A destructive mutations. Locally duplicated from the realtyList
// wrappers per SR-6 (Local Duplication Is Preferred) — the detail
// feature must not import from features/realtyList. Single-shot,
// no retry, no cache invalidation, no entity store sync. Backend truth
// is authoritative; callers await the response before reflecting state.
export const patchPropertyStatus = (id, status) =>
  api
    .patch(`${REALTY_BASE}/properties/${id}/`, { status })
    .then((res) => res.data);

export const deleteProperty = (id) =>
  api.delete(`${REALTY_BASE}/properties/${id}/`).then((res) => res.data);

// R3.12A complaint catalog. Single-shot, no retry, no cache. Local
// per SR-6 — the detail feature must not import from another feature's
// internals. Pre-R8.6 this block also wrapped /users/profile/follow/;
// R8.6 removed that wrapper because follow now flows through the
// canonical Redux thunk (see header comment for rationale).
export const fetchComplaintCategories = () =>
  api.get("/complaints/categories/").then((res) => res.data);

// Submit a property complaint. Backend validates exactly one of
// target_user_id / target_property_id is supplied; self-targeting
// guards live in apps/complaint/services.file_complaint.
export const submitPropertyComplaint = ({
  categoryId,
  description,
  propertyId,
}) =>
  api
    .post("/complaints/", {
      category_id: categoryId,
      description: description || "",
      target_property_id: propertyId,
    })
    .then((res) => res.data);
